import { SegmentedControl, CardHeader as ContentCardHeader, IconButton } from '@hue/design-system';
import { ClipboardList as ContentCardIcon, Eye, PencilLine } from 'lucide-react';
import {
  carryAnswers,
  reviewAnswers,
  type RecordAnswer,
  type RecordAnswers,
  type RecordQuestion,
  type YesNoAnswer,
} from '@hue/contracts';
import { Button, FormErrorSummary, StatusPill, useSnackbar } from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import {
  getApiError,
  getApiIssues,
  recordsApi,
  type ClientChart,
  type ClientChartTemplate,
  type ClientRecord,
} from '../../lib/api.js';
import { formatDate } from '../../lib/time-zone.js';
import { ErrorCard, LoadingCard, RequestAlert } from '../../app/data-view.js';
import { HealthPanel } from './health-panel.js';
import { RecordForm } from './record-form.js';
import { RecordView } from './record-view.js';
import { Notice } from './shared.js';
import { PrintButton, SignaturePill, SignatureSection } from './signature.js';
import { Timeline, type TimelineFilter } from './timeline.js';
import { ClientPhotos } from './client-photos.js';
import { Images } from 'lucide-react';
import { blocksOf, chartQueryKey, themeOf } from './helpers.js';

export interface ClientRecordsView {
  section: 'timeline' | 'records' | 'photos';
  photoAppointment?: string | undefined;
  /// O modelo da ficha aberta.
  record?: string | undefined;
  version?: number | undefined;
  compare?: number | undefined;
  edit: boolean;
  timeline: TimelineFilter;
  full: boolean;
}

/// A aba Prontuário da página do cliente (ADR 0038): o Quadro de saúde ao lado, e a Linha do tempo
/// ou as fichas no centro. A ficha aberta, a versão e a comparação moram na URL.
export function ClientRecords({
  clientId,
  timeZone,
  today,
  view,
  onView,
  canManage,
  consents,
}: {
  clientId: string;
  timeZone: string;
  today: string;
  view: ClientRecordsView;
  onView: (patch: Partial<ClientRecordsView>) => void;
  canManage: boolean;
  /// As autorizações do cadastro, montadas pela composição: moram em Clientes, e a ficha e a tela
  /// de assinar as mostram (ADR 0040, decisão 6).
  consents?: ReactNode;
}) {
  const queryClient = useQueryClient();
  const chart = useQuery({
    queryKey: chartQueryKey(clientId),
    queryFn: () => recordsApi.getChart(clientId),
    retry: false,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['company', 'records'] });
  const open = (templateId: string, edit: boolean) =>
    onView({ record: templateId, edit, version: undefined, compare: undefined });

  if (chart.isLoading) return <LoadingCard label="Carregando prontuário" />;
  if (chart.error || !chart.data) return <ErrorCard message={getApiError(chart.error)} />;
  const data = chart.data;
  const template = data.templates.find((item) => item.id === view.record);
  const record = data.records.find((item) => item.templateId === view.record);

  const openPhotos = (appointmentId?: string) =>
    onView({ section: 'photos', photoAppointment: appointmentId });
  const photos =
    view.section === 'photos' ? (
      <ClientPhotos
        key={view.photoAppointment ?? 'all'}
        clientId={clientId}
        timeZone={timeZone}
        canManage={canManage}
        sessions={data.sessions ?? []}
        initialAppointmentId={view.photoAppointment}
        onBack={() =>
          onView({ section: template ? 'records' : 'timeline', photoAppointment: undefined })
        }
      />
    ) : null;

  let body;
  if (template && view.edit && canManage) {
    body = (
      <RecordEditor
        key={`${template.id}-${String(record?.currentVersion ?? 0)}`}
        chart={data}
        template={template}
        record={record}
        today={today}
        consents={consents}
        onCancel={() => onView({ edit: false, ...(record ? {} : { record: undefined }) })}
        onSaved={async () => {
          await refresh();
          onView({ edit: false, version: undefined, compare: undefined });
        }}
      />
    );
  } else if (template && record) {
    body = (
      <RecordView
        chart={data}
        record={record}
        version={view.version ?? null}
        compare={view.compare ?? null}
        timeZone={timeZone}
        canManage={canManage}
        onSelectVersion={(version) => onView({ version, compare: undefined })}
        onCompare={(compare) => onView({ compare: compare ?? undefined })}
        onEdit={() => onView({ edit: true })}
        onBack={() => onView({ record: undefined, version: undefined, compare: undefined })}
        onSigned={async () => {
          await refresh();
        }}
        consents={consents}
      />
    );
  } else {
    body = (
      <div className="grid gap-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SegmentedControl
            tabs
            label="Prontuário"
            value={view.section}
            onChange={(section) => onView({ section })}
            options={[
              { value: 'timeline', label: 'Linha do tempo' },
              { value: 'records', label: 'Fichas' },
              { value: 'photos', label: 'Fotos e evolução' },
            ]}
          />
          {data.records.length ? (
            <PrintButton
              scope={{ kind: 'CHART', clientId }}
              fileParts={['prontuario', data.client.displayName]}
              label="PDF do prontuário"
            />
          ) : null}
        </div>
        {view.section === 'timeline' ? (
          <Timeline
            chart={data}
            timeZone={timeZone}
            filter={view.timeline}
            full={view.full}
            onFilter={(timeline) => onView({ timeline })}
            onFull={(full) => onView({ full })}
            onOpenPhotos={openPhotos}
            onOpenVersion={(templateId, version) =>
              onView({ record: templateId, version, edit: false, compare: undefined })
            }
          />
        ) : (
          <>
            <RecordCards chart={data} timeZone={timeZone} canManage={canManage} onOpen={open} />
            {data.records.length ? (
              <SignatureSection
                scope={{ kind: 'CHART', clientId }}
                heading="Prontuário assinado"
                documents={data.documents.filter((document) => document.kind === 'CHART')}
                pendingText={
                  data.documents.some((document) => document.kind === 'CHART')
                    ? null
                    : 'O prontuário inteiro ainda não foi assinado. Ele reúne a versão atual de cada ficha e os pontos de atenção.'
                }
                pending={false}
                signable={{ screen: true, paper: true }}
                clientName={data.client.displayName}
                title="Prontuário"
                timeZone={timeZone}
                canManage={canManage}
                consents={consents}
                onSigned={async () => {
                  await refresh();
                }}
              />
            ) : null}
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <div hidden={Boolean(photos)}>
        <div className="grid gap-5 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] lg:items-start">
          <HealthPanel
            chart={data}
            timeZone={timeZone}
            canManage={canManage}
            onOpenRecord={open}
            onChanged={async () => {
              await refresh();
            }}
          />
          {/* Com uma ficha aberta, no celular ela vem antes do Quadro: é o que a pessoa foi ver. */}
          <div className={template ? 'order-first min-w-0 lg:order-none' : 'min-w-0'}>
            <Button size="sm" variant="outline" className="mb-3" onClick={() => openPhotos()}>
              <Images className="size-4" /> Fotos e evolução
            </Button>
            {body}
          </div>
        </div>
      </div>
      {photos}
    </>
  );
}

function RecordCards({
  chart,
  timeZone,
  canManage,
  onOpen,
}: {
  chart: ClientChart;
  timeZone: string;
  canManage: boolean;
  onOpen: (templateId: string, edit: boolean) => void;
}) {
  const applicable = chart.templates.filter((template) => template.applicable);
  const others = chart.templates.filter((template) => !template.applicable && template.active);
  const serviceNames = (template: ClientChartTemplate) =>
    template.serviceIds
      .map((id) => chart.services?.find((service) => service.id === id)?.displayName)
      .filter(Boolean)
      .join(', ');
  if (!chart.templates.length) {
    return (
      <Notice tone="info">
        Nenhum modelo de ficha publicado ainda. Quem monta os modelos cria em Prontuário ▸ Modelos
        de ficha.
      </Notice>
    );
  }
  return (
    <div className="grid gap-4">
      <div className="grid gap-3 sm:grid-cols-2">
        {applicable.map((template) => {
          const record = chart.records.find((item) => item.templateId === template.id);
          const current = record?.versions.at(-1);
          return (
            <article
              key={template.id}
              className="grid gap-2 rounded-2xl border border-surface-border bg-surface-card p-4 shadow-soft"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-semibold">{template.displayName}</h3>
                {current ? (
                  <span className="flex flex-wrap justify-end gap-1.5">
                    <StatusPill tone="positive">Versão {current.version}</StatusPill>
                    <SignaturePill
                      template={template}
                      versionId={current.id}
                      documents={chart.documents}
                    />
                  </span>
                ) : (
                  <StatusPill tone="neutral">Não preenchida</StatusPill>
                )}
              </div>
              {record && current ? (
                <>
                  <p className="text-xs text-content-muted">
                    {formatDate(current.recordedAt, timeZone)} · {current.recordedByDisplayName} ·{' '}
                    {record.versions.length} {record.versions.length === 1 ? 'versão' : 'versões'}
                  </p>
                  {blocksVersion(chart, current.templateVersionId) < template.latestVersion ? (
                    <p className="text-xs text-warning">
                      Respondida no modelo v{blocksVersion(chart, current.templateVersionId)}; em
                      uso: v{template.latestVersion}.
                    </p>
                  ) : null}
                </>
              ) : (
                <p className="text-xs text-content-muted">
                  {template.allServices || !chart.services
                    ? 'Vale para todos os serviços.'
                    : `Aplicável porque ela faz ${serviceNames(template)}.`}
                </p>
              )}
              <div className="flex flex-wrap gap-1">
                {record ? (
                  <IconButton
                    label="Abrir"
                    icon={<Eye />}
                    onClick={() => onOpen(template.id, false)}
                  />
                ) : null}
                {canManage && template.active ? (
                  <IconButton
                    label={record ? 'Editar' : 'Preencher'}
                    icon={<PencilLine />}
                    onClick={() => onOpen(template.id, true)}
                  />
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
      {others.length && canManage ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-content-muted">Preencher outra ficha:</span>
          {others.map((template) => (
            <Button
              key={template.id}
              size="sm"
              variant="ghost"
              onClick={() => onOpen(template.id, true)}
            >
              <Plus className="size-4" />
              {template.displayName}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function blocksVersion(chart: ClientChart, templateVersionId: string): number {
  return chart.templateVersions.find((version) => version.id === templateVersionId)!.version;
}

/// Preencher ou editar a ficha grava uma versão nova, sempre no modelo em uso: as respostas das
/// perguntas que continuam vêm preenchidas (ADR 0038, decisão 4).
export function RecordEditor({
  chart,
  template,
  record,
  today,
  consents,
  onCancel,
  onSaved,
}: {
  chart: ClientChart;
  template: ClientChartTemplate;
  record: ClientRecord | undefined;
  today: string;
  consents?: ReactNode;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const formId = useId();
  const snackbar = useSnackbar();
  const blocks = blocksOf(chart, template.latestVersionId);
  const current = record?.versions.at(-1);
  const [answers, setAnswers] = useState<RecordAnswers>(() =>
    current ? carryAnswers(blocks, current.answers) : {},
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [requestError, setRequestError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const next = (record?.currentVersion ?? 0) + 1;

  function hintFor(question: RecordQuestion, answer: RecordAnswer | undefined) {
    if (!question.themeKey) return null;
    const others = chart.health.declarations.filter(
      (item) => item.themeKey === question.themeKey && item.templateId !== template.id,
    );
    if (!others.length) return null;
    const theme = themeOf(chart.themes, question.themeKey);
    const contradicts =
      question.type === 'YES_NO' && (answer as YesNoAnswer | undefined)?.value === false;
    return (
      <p
        className={`rounded-lg border px-3 py-2 text-xs leading-5 ${
          contradicts ? 'border-warning/40 bg-warning/10' : 'border-info/30 bg-info/10'
        }`}
      >
        {contradicts ? <strong>Diverge do quadro. </strong> : null}
        {others.map((item, index) => (
          <span key={`${item.templateId}-${item.questionId}`}>
            {index ? '; ' : ''}
            {theme.displayName}: <strong>{item.value}</strong> na ficha {item.templateName}
          </span>
        ))}
        .
      </p>
    );
  }

  async function submit() {
    setRequestError(undefined);
    const review = reviewAnswers(blocks, answers);
    const found = Object.fromEntries(
      review.issues.map((issue) => [issue.blockId ?? '', issue.message]),
    );
    setErrors(found);
    if (review.issues.length) return;
    setSaving(true);
    try {
      await recordsApi.recordVersion(chart.client.id, {
        templateId: template.id,
        templateVersionId: template.latestVersionId,
        expectedVersion: record?.currentVersion ?? 0,
        answers: review.answers,
      });
      await onSaved();
      snackbar.success(`${template.displayName}: versão ${String(next)} gravada.`);
    } catch (error) {
      const issues = getApiIssues(error);
      if (issues.length) {
        setErrors(Object.fromEntries(issues.map((issue) => [issue.blockId ?? '', issue.message])));
      } else setRequestError(getApiError(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid gap-4 rounded-2xl border border-surface-border bg-surface-card p-5 shadow-soft">
      <div className="grid gap-1">
        <ContentCardHeader
          title={`${record ? 'Editar' : 'Preencher'} ${template.displayName}`}
          icon={<ContentCardIcon className="size-4" />}
        />
        <p className="text-xs text-content-muted">
          Modelo v{template.latestVersion}. Salvar grava a versão {next}
          {record ? ` e mantém a ${String(record.currentVersion)} no histórico` : ''}.
          {template.signaturePolicy === 'EVERY_VERSION' ? ' Este modelo pede assinatura.' : ''}
        </p>
      </div>
      {current && chart.documents.some((document) => document.versionIds.includes(current.id)) ? (
        <Notice tone="info">
          A versão {current.version} está num documento assinado. A nova versão nasce sem
          assinatura, e o documento continua valendo.
        </Notice>
      ) : null}
      <form
        id={formId}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        className="grid gap-5"
      >
        <div className="rounded-xl border border-surface-border bg-surface-elevated/40 p-4 sm:p-5">
          <RecordForm
            blocks={blocks}
            answers={answers}
            onChange={(value) => {
              setAnswers(value);
              setErrors({});
            }}
            errors={errors}
            today={today}
            hintFor={hintFor}
          />
        </div>
        {consents}
        {requestError ? <RequestAlert message={requestError} /> : null}
        <div className="flex flex-wrap items-center justify-end gap-3">
          <FormErrorSummary className="mr-auto" />
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" loading={saving}>
            Salvar versão {next}
          </Button>
        </div>
      </form>
    </div>
  );
}
